<template>
  <!-- Fixed decorative layer behind every public page (never blocks clicks).
       Assamese motifs: woven gamosa medallions, a gamosa end, and a jaapi. -->
  <div ref="root" class="decor-root" aria-hidden="true">
    <div
      v-for="item in items"
      :key="item.id"
      :class="['decor-item', item.pos, item.size, item.mobile ? '' : 'hidden md:block']"
      :style="{ '--decor-opacity': item.opacity }"
    >
      <div class="decor-parallax" :data-depth="item.depth">
        <div class="decor-tilt" :data-depth="item.depth" :data-rx="item.rx" :data-ry="item.ry" :style="{ '--rx': `${item.rx}deg`, '--ry': `${item.ry}deg` }">
          <div class="decor-float">
            <div v-if="item.glow" class="decor-glow"></div>
            <div :class="['decor-art', item.kind]" :style="{ '--rz': `${item.rz || 0}deg` }">
              <img :src="item.src" alt="" class="decor-img" draggable="false" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import medallionRed from '@/assets/gamosa/medallion-red.svg'
import medallionGold from '@/assets/gamosa/medallion-gold.svg'
import gamosaCloth from '@/assets/gamosa/cloth.svg'
import jaapi from '@/assets/images/japi2.png'

gsap.registerPlugin(ScrollTrigger)

// Each piece: image, screen corner, size, starting 3D tilt, depth (how strongly it reacts), look
const items = [
  { id: 1, src: medallionRed, pos: 'pos-tl', size: 'size-medallion', rx: 16, ry: 26, depth: 1.0,
    kind: 'kind-woven-red', glow: true, opacity: 0.55, mobile: false },
  { id: 2, src: jaapi, pos: 'pos-tr', size: 'size-jaapi', rx: 22, ry: -24, depth: 0.7,
    kind: 'kind-object', glow: true, opacity: 0.85, mobile: true },
  { id: 3, src: gamosaCloth, pos: 'pos-bl', size: 'size-cloth', rx: 10, ry: 28, rz: -14, depth: 0.8,
    kind: 'kind-cloth', glow: false, opacity: 0.9, mobile: true },
  { id: 4, src: medallionGold, pos: 'pos-br', size: 'size-medallion', rx: -14, ry: -24, depth: 1.2,
    kind: 'kind-woven-gold', glow: true, opacity: 0.6, mobile: false },
]

const root = ref(null)
let ctx
let onMouseMove

onMounted(() => {
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

  ctx = gsap.context(() => {
    // 1. Gentle floating, each piece on its own rhythm (desktop only, saves battery on phones)
    const floatEls = window.matchMedia('(min-width: 768px)').matches ? gsap.utils.toArray('.decor-float') : []
    floatEls.forEach((el, i) => {
      gsap.to(el, {
        y: i % 2 ? 16 : -16,
        rotation: i % 2 ? -6 : 6,
        duration: 5 + i * 1.3,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
      })
    })

    // 2. Slow drift while scrolling (deeper pieces move more; the jaapi slowly turns)
    gsap.utils.toArray('.decor-parallax').forEach((el) => {
      const depth = parseFloat(el.dataset.depth)
      gsap.to(el, {
        y: -90 * depth,
        rotation: 18 * depth,
        ease: 'none',
        scrollTrigger: { trigger: document.documentElement, start: 'top top', end: 'bottom bottom', scrub: 1.2 },
      })
    })

    // 3. 3D tilt that follows the mouse
    const tilts = gsap.utils.toArray('.decor-tilt').map((el) => {
      const depth = parseFloat(el.dataset.depth)
      return {
        depth,
        baseX: parseFloat(el.dataset.rx),
        baseY: parseFloat(el.dataset.ry),
        rx: gsap.quickTo(el, 'rotationX', { duration: 1.1, ease: 'power3.out' }),
        ry: gsap.quickTo(el, 'rotationY', { duration: 1.1, ease: 'power3.out' }),
        x: gsap.quickTo(el, 'x', { duration: 1.4, ease: 'power3.out' }),
        y: gsap.quickTo(el, 'y', { duration: 1.4, ease: 'power3.out' }),
      }
    })
    onMouseMove = (e) => {
      const nx = (e.clientX / window.innerWidth) * 2 - 1
      const ny = (e.clientY / window.innerHeight) * 2 - 1
      tilts.forEach((t) => {
        t.ry(t.baseY + nx * 14 * t.depth)
        t.rx(t.baseX - ny * 12 * t.depth)
        t.x(nx * 22 * t.depth)
        t.y(ny * 16 * t.depth)
      })
    }
    window.addEventListener('mousemove', onMouseMove, { passive: true })
  }, root.value)
})

onBeforeUnmount(() => {
  if (onMouseMove) window.removeEventListener('mousemove', onMouseMove)
  if (ctx) ctx.revert()
})
</script>

<style scoped>
.decor-root {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
  /* Faint woven cloth with scattered butta motifs, like the plain body of a gamosa */
  background-image: url('@/assets/gamosa/pattern.svg');
  background-size: 120px 120px;
}

.decor-item {
  position: absolute;
  perspective: 900px;
  opacity: var(--decor-opacity, 0.7);
}
.size-medallion { width: clamp(200px, 21vw, 360px); aspect-ratio: 1; }
.size-jaapi { width: clamp(170px, 17vw, 290px); aspect-ratio: 1; }
.size-cloth { width: clamp(150px, 14vw, 240px); aspect-ratio: 80 / 112; }

.pos-tl { left: -8vw; top: 3vh; }
.pos-tr { right: -3vw; top: 16vh; }
.pos-bl { left: 1.5vw; bottom: -4vh; }
.pos-br { right: -7vw; bottom: -9vh; }

@media (max-width: 767px) {
  .decor-item {
    opacity: calc(var(--decor-opacity, 0.7) * 0.7);
  }
  .size-jaapi { width: 42vw; }
  .size-cloth { width: 34vw; }
  .pos-tr { right: -14vw; top: 13vh; }
  .pos-bl { left: -8vw; bottom: -6vh; }
}

.decor-parallax,
.decor-tilt,
.decor-float,
.decor-art {
  position: absolute;
  inset: 0;
  transform-style: preserve-3d;
}

/* Starting tilt; GSAP adds the mouse-driven part on top */
.decor-tilt {
  transform: rotateX(var(--rx)) rotateY(var(--ry));
  will-change: transform;
}

/* Soft cream disc behind a piece gives it something to "float" above */
.decor-glow {
  position: absolute;
  inset: 14%;
  border-radius: 9999px;
  background: radial-gradient(circle at 35% 30%, rgba(255, 233, 160, 0.95), rgba(255, 233, 160, 0.3) 55%, transparent 72%);
  filter: blur(18px);
  transform: translateZ(-60px);
}

.decor-art {
  transform: translateZ(30px) rotate(var(--rz, 0deg));
}

.decor-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
}

/* Stacked drop-shadows make the woven threads look raised, like embroidery */
.kind-woven-red {
  filter:
    drop-shadow(1px 1px 0 rgba(120, 18, 18, 0.55))
    drop-shadow(1px 1px 0 rgba(110, 16, 16, 0.45))
    drop-shadow(1px 2px 0 rgba(90, 12, 12, 0.35))
    drop-shadow(8px 16px 16px rgba(70, 20, 20, 0.22));
}
.kind-woven-gold {
  filter:
    drop-shadow(1px 1px 0 rgba(120, 78, 12, 0.55))
    drop-shadow(1px 1px 0 rgba(105, 66, 10, 0.45))
    drop-shadow(1px 2px 0 rgba(85, 52, 8, 0.35))
    drop-shadow(8px 16px 16px rgba(70, 45, 10, 0.22));
}

/* Real objects: a soft contact shadow underneath */
.kind-object {
  filter:
    drop-shadow(0 4px 6px rgba(60, 30, 10, 0.22))
    drop-shadow(0 22px 26px rgba(60, 30, 10, 0.28));
}

/* The gamosa end fades in from the top, as if the cloth continues out of view */
.kind-cloth {
  filter:
    drop-shadow(0 3px 4px rgba(60, 30, 10, 0.18))
    drop-shadow(10px 22px 24px rgba(60, 30, 10, 0.25));
}
.kind-cloth .decor-img {
  -webkit-mask-image: linear-gradient(to bottom, transparent 0%, #000 30%);
  mask-image: linear-gradient(to bottom, transparent 0%, #000 30%);
}
</style>
